<template>
  <div class="customer-map-container">
    <div class="page-header">
      <div class="page-title-block">
        <h2>客户地图</h2>
        <p class="page-subtitle">在地图上快速查看客户分布，并联动列表筛选与导航</p>
      </div>
    </div>
    
    <el-card class="map-card">
      <!-- 上方大地图 -->
      <div class="map-container">
        <el-amap
          :center="mapCenter"
          :zoom="12"
          :scroll-wheel-zoom="true"
          class="map"
          style="height: 500px; width: 100%;"
          :events="amapEvents"
        >
          <!-- 客户标记点 -->
          <el-amap-marker
            v-for="customer in validCustomers"
            :key="customer.id"
            :position="[parseFloat(customer.longitude), parseFloat(customer.latitude)]"
            :events="{
              click: () => handleMarkerClick(customer)
            }"
          >
            <!-- 使用内联样式，避免 vue-amap 渲染导致 scoped 样式失效 -->
            <div
              style="
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background: rgba(64, 158, 255, 0.22);
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 0 14px rgba(64, 158, 255, 0.7);
              "
            >
              <i
                class="el-icon-user-solid"
                style="font-size: 34px; color: #ffffff;"
              ></i>
            </div>
          </el-amap-marker>
          
          <!-- 信息窗口 -->
          <el-amap-info-window
            v-if="selectedCustomer && selectedCustomer.longitude && selectedCustomer.latitude && !isNaN(parseFloat(selectedCustomer.longitude)) && !isNaN(parseFloat(selectedCustomer.latitude))"
            :position="[parseFloat(selectedCustomer.longitude), parseFloat(selectedCustomer.latitude)]"
            :visible="!!selectedCustomer"
            :events="{
              close: handleInfoWindowClose
            }"
          >
            <div class="info-window-content">
              <h3>{{ selectedCustomer.name }}</h3>
              <p>电话: {{ selectedCustomer.phone }}</p>
              <p>地址: {{ selectedCustomer.address }}</p>
              <div class="info-window-actions">
                <el-button type="primary" size="mini" @click="handleViewCustomer(selectedCustomer)">
                  查看详情
                </el-button>
                <el-button size="mini" @click="handleNavigate(selectedCustomer)">
                  导航
                </el-button>
              </div>
            </div>
          </el-amap-info-window>
        </el-amap>
      </div>
      
      <!-- 下方客户列表 -->
      <div class="customer-list-sidebar">
          <div class="customer-list-header">
            <div class="header-left">
              <h3>客户列表</h3>
              <span class="count-tag">共 {{ total }} 个客户</span>
            </div>
            <el-input
              v-model="searchKeyword"
              placeholder="搜索客户名称、电话或地址"
              clearable
              size="small"
              class="search-input"
            >
              <template slot="prepend">
                <i class="el-icon-search"></i>
              </template>
            </el-input>
          </div>
          <div class="customer-list-content" v-if="paginatedCustomers.length">
            <el-card
              v-for="customer in paginatedCustomers"
              :key="customer.id"
              class="customer-item-card"
              :class="{ 'selected': selectedCustomer && selectedCustomer.id === customer.id }"
              shadow="hover"
              @click.native="handleCustomerItemClick(customer)"
            >
              <div class="customer-item">
                <h4>
                  <span class="customer-name">{{ customer.name }}</span>
                </h4>
                <p>
                  <i class="el-icon-phone-outline"></i>
                  <span>{{ customer.phone || '暂无电话' }}</span>
                </p>
                <p class="address">
                  <i class="el-icon-location-outline"></i>
                  <span>{{ customer.address || '暂无地址' }}</span>
                </p>
                <div class="customer-item-actions">
                  <el-button type="primary" size="mini" @click.stop="handleViewCustomer(customer)">
                    <i class="el-icon-view"></i> 查看
                  </el-button>
                </div>
              </div>
            </el-card>
          </div>
          <div class="empty-tip" v-else>
            暂无符合条件的客户
          </div>
          
          <!-- 分页组件 -->
          <div class="customer-list-pagination" v-if="total > 0">
            <el-pagination
              background
              :current-page="currentPage"
              :page-size="pageSize"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            ></el-pagination>
          </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import request from '../utils/request'
import { getUserInfo } from '../utils/auth'
import { filterCustomersByDataPermission } from '../utils/dataPermission'

export default {
  name: 'CustomerMap',
  data() {
    return {
      customers: [], // 客户列表
      selectedCustomer: null, // 当前选中的客户
      mapLoaded: false, // 地图加载状态
      mapCenter: [116.397428, 39.90923], // 地图中心点
      searchKeyword: '', // 搜索关键词
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页显示数量
      total: 0, // 总数据量
      currentUser: null, // 当前登录用户
      isMobile: false,
      controlsInitedMapSet: null,
      amapEvents: {
        init: (mapInstance) => this.handleMapInit(mapInstance),
        complete: () => this.handleMapReady()
      }
    }
  },
  watch: {
    // 监听过滤后的客户列表变化，更新总数据量
    filteredCustomers: {
      handler(newVal) {
        this.total = newVal.length
        // 如果数据量变化导致当前页码超过总页数，重置到第一页
        const totalPages = Math.ceil(newVal.length / this.pageSize)
        if (this.currentPage > totalPages) {
          this.currentPage = 1
        }
      },
      immediate: true
    }
  },
  computed: {
    // 过滤出有有效经纬度的客户
    validCustomers() {
      return this.customers.filter(customer => {
        return customer.longitude && 
               customer.latitude && 
               !isNaN(parseFloat(customer.longitude)) && 
               !isNaN(parseFloat(customer.latitude))
      })
    },
    // 根据搜索关键词过滤客户列表
    filteredCustomers() {
      if (!this.searchKeyword) {
        return this.customers
      }
      const keyword = this.searchKeyword.toLowerCase()
      return this.customers.filter(customer => {
        return customer.name.toLowerCase().includes(keyword) ||
               customer.phone.includes(keyword) ||
               customer.address.toLowerCase().includes(keyword)
      })
    },
    // 分页后的客户列表
    paginatedCustomers() {
      // 计算分页数据
      const startIndex = (this.currentPage - 1) * this.pageSize
      const endIndex = startIndex + this.pageSize
      return this.filteredCustomers.slice(startIndex, endIndex)
    }
  },
  mounted() {
    // 读取当前用户信息并加载客户数据
    this.currentUser = getUserInfo()
    this.controlsInitedMapSet = new Set()
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    this.loadCustomers()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    // 加载客户数据
    loadCustomers() {
      request.get('/customers')
        .then(response => {
          const all = response.data || response
          this.customers = filterCustomersByDataPermission(all || [], this.currentUser)
          console.log('客户数据加载成功:', this.customers)
          console.log('有效客户数据:', this.validCustomers)

          // 根据首个有效客户设置地图中心
          if (this.validCustomers && this.validCustomers.length > 0) {
            const first = this.validCustomers[0]
            if (first.longitude && first.latitude) {
              this.mapCenter = [parseFloat(first.longitude), parseFloat(first.latitude)]
            }
          }
        })
        .catch(error => {
          console.error('加载客户列表失败:', error)
          this.$message.error('加载客户列表失败')
        })
    },
    
    // 点击标记点
    handleMarkerClick(customer) {
      this.selectedCustomer = customer
    },
    
    // 关闭信息窗口
    handleInfoWindowClose() {
      this.selectedCustomer = null
    },
    
    // 点击客户列表项
    handleCustomerItemClick(customer) {
      this.selectedCustomer = customer
      // 如果客户有有效经纬度，设置地图中心
      if (customer.longitude && customer.latitude && !isNaN(parseFloat(customer.longitude)) && !isNaN(parseFloat(customer.latitude))) {
        this.mapCenter = [parseFloat(customer.longitude), parseFloat(customer.latitude)]
      }
    },
    
    // 查看客户详情
    handleViewCustomer(customer) {
      this.$router.push(`/customers/${customer.id}`)
    },

    // 导航到客户位置（优先唤醒高德 App，失败则打开网页导航）
    handleNavigate(customer) {
      const lon = parseFloat(customer && customer.longitude)
      const lat = parseFloat(customer && customer.latitude)
      if (!lon || !lat || Number.isNaN(lon) || Number.isNaN(lat)) {
        this.$message.warning('客户坐标无效，无法导航')
        return
      }

      const name = encodeURIComponent((customer && customer.name) || '目的地')
      const webUrl = `https://uri.amap.com/navigation?to=${lon},${lat},${name}&mode=car&policy=1&src=khgl&coordinate=gaode&callnative=1`

      const ua = navigator.userAgent || ''
      const isMobile = /Android|iPhone|iPad|iPod/i.test(ua)
      if (!isMobile) {
        window.open(webUrl, '_blank', 'noopener')
        return
      }

      // 移动端尝试唤醒 App scheme，失败后自动回退网页导航
      const schemeUrl = `amapuri://route/plan/?dlat=${lat}&dlon=${lon}&dname=${name}&t=0`
      const start = Date.now()
      window.location.href = schemeUrl
      setTimeout(() => {
        if (Date.now() - start < 1600) {
          window.location.href = webUrl
        }
      }, 1200)
    },
    
    // 处理每页显示数量变化
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    
    // 处理页码变化
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    // 地图加载完成事件
    handleMapReady() {
      this.mapLoaded = true
      console.log('地图加载完成')
    },
    
    // 地图初始化事件
    handleMapInit(mapInstance) {
      console.log('地图初始化完成，地图实例:', mapInstance)
      this.initMapControls(mapInstance)
      // 尝试手动设置地图中心点和缩放级别
      if (mapInstance && this.validCustomers.length > 0) {
        const firstCustomer = this.validCustomers[0]
        mapInstance.setCenter([parseFloat(firstCustomer.longitude), parseFloat(firstCustomer.latitude)])
        mapInstance.setZoom(12)
      }
    },

    initMapControls(mapInstance) {
      if (!window.AMap || !mapInstance || !mapInstance.addControl) return
      if (this.controlsInitedMapSet && this.controlsInitedMapSet.has(mapInstance)) return
      if (this.controlsInitedMapSet) this.controlsInitedMapSet.add(mapInstance)

      try {
        if (window.AMap.ToolBar) {
          mapInstance.addControl(new window.AMap.ToolBar({ position: 'RB', liteStyle: false }))
        }
        if (window.AMap.Scale) {
          mapInstance.addControl(new window.AMap.Scale({ position: 'LB' }))
        }
      } catch (e) {
        console.error('地图控件初始化失败:', e)
      }
    },

  }
}
</script>

<style scoped>
.customer-map-container {
  width: 100%;
  padding: 0;
}

.page-header {
  margin-bottom: 18px;
  padding: 6px 4px 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.page-header h2 {
  margin: 0;
  color: #0f8f59;
  font-size: 24px;
  font-weight: 600;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #80848f;
}

/* 全屏弹窗：撑满并去掉默认内边距 */

.map-card {
  border-radius: 16px;
  box-shadow: 0 18px 40px rgba(15, 143, 89, 0.08);
  border: none;
  padding-bottom: 10px;
}

.map-container {
  margin-bottom: 16px;
  position: relative;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.map {
  width: 100%;
  height: 100%;
}

.customer-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgba(64, 158, 255, 0.16);
  box-shadow: 0 0 10px rgba(64, 158, 255, 0.55);
}

.customer-marker i {
  color: #409eff;
  font-size: 28px;
}

.info-window-content {
  padding: 10px;
  max-width: 260px;
}

.info-window-content h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.info-window-content p {
  margin-bottom: 5px;
}

.info-window-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.customer-list-sidebar {
  display: flex;
  flex-direction: column;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  padding: 14px 14px 10px;
  background-color: #f5f7fa;
}

.customer-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.customer-list-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.customer-list-header .header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-tag {
  font-size: 12px;
  color: #909399;
}

.search-input {
  width: 230px;
}

.customer-list-content {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
  max-height: 340px;
  overflow-y: auto;
  padding-right: 4px;
}

.customer-item-card {
  border-radius: 12px;
  border: none;
}

.customer-item {
  padding: 12px 12px 10px;
  background-color: #fff;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.customer-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
  border-color: #409eff;
}

.customer-item.selected {
  border-color: #409eff;
  box-shadow: 0 4px 16px 0 rgba(64, 158, 255, 0.2);
  background-color: #f0f7ff;
}

.customer-item.selected::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background-color: #409eff;
}

.customer-item h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.customer-item h4::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #409eff;
  border-radius: 50%;
  margin-right: 8px;
}

.customer-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-item p {
  margin: 0 0 8px 0;
  color: #606266;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.customer-item p i {
  color: #909399;
  font-size: 14px;
}

.customer-item .address {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 5px;
}

.customer-item-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
}

.customer-item-actions .el-button {
  padding: 4px 12px;
  font-size: 12px;
}

.customer-list-pagination {
  margin-top: 10px;
  display: flex;
  justify-content: center;
  padding: 15px 0;
}

.empty-tip {
  text-align: center;
  padding: 40px 10px 20px;
  color: #909399;
  font-size: 13px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .map-container {
    margin-bottom: 12px;
  }
  
  .customer-list-sidebar {
    margin-top: 15px;
    padding: 12px 10px;
  }
  
  .customer-list-sidebar h3 {
    font-size: 16px;
  }
  
  .customer-item {
    padding: 10px;
  }
  
  .customer-item h4 {
    font-size: 14px;
  }
  
  .customer-item p {
    font-size: 12px;
  }
}